import type { CatalogItem, Plan, PlanBenefitInput, PlanInput, PlanKind } from "@vaulted/shared";
import { ITEM_TYPE_LABELS, VOUCHER_EXPIRY_CHOICES, describeBenefit } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { errorMessage, get, post, put } from "../api";
import { AgreementText } from "../components/Agreement";
import { Alert, Badge, Button, Checkbox, Field, Loading, MoneyField, Panel, PanelHeader, SelectField, TextAreaField, money } from "../components/ui";
import { useSession } from "../session";
import { type CatalogResponse, useCatalog } from "./Catalog";
import { BackLink } from "../components/BackLink";

export function usePlans() {
  return useQuery({ queryKey: ["plans"], queryFn: () => get<Plan[]>("/api/plans") });
}

export const PLAN_PATHS: Record<PlanKind, string> = { membership: "memberships", package: "packages" };
const PATH_KINDS: Record<string, PlanKind> = { memberships: "membership", packages: "package" };

/** Names for benefit summaries: items and categories by id. */
export function useNameOf(data: CatalogResponse | undefined) {
  return useMemo(() => {
    const names = new Map<string, string>();
    for (const i of data?.items ?? []) names.set(i.id, i.name);
    for (const c of data?.categories ?? []) names.set(c.id, c.name);
    return (id: string) => names.get(id) ?? "(removed)";
  }, [data]);
}

export const priceLabel = (p: Pick<Plan, "kind" | "priceCents">) => `${money(p.priceCents)}${p.kind === "membership" ? "/month" : ""}`;

/** Catalog → Memberships / Packages. */
export function PlansTab({ kind, data }: { kind: PlanKind; data: CatalogResponse }) {
  const { can } = useSession();
  const plans = usePlans();
  const [archived, setArchived] = useState(false);
  const nameOf = useNameOf(data);
  const labels = ITEM_TYPE_LABELS[kind];
  if (plans.isLoading) return <Loading />;
  if (plans.error || !plans.data) return <Alert>{errorMessage(plans.error)}</Alert>;
  const ofKind = plans.data.filter((p) => p.kind === kind);
  const list = ofKind.filter((p) => (archived ? p.status === "archived" : p.status === "active"));
  const archivedCount = ofKind.filter((p) => p.status === "archived").length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-ink-soft">
          {kind === "membership"
            ? "Monthly plans billed to the client's card. Vouchers and member pricing apply at clinic visits."
            : "Prepaid visits, paid once. Vouchers apply at clinic visits."}
        </p>
        {can("catalog.manage") && (
          <Link to={`/catalog/plan/new/${PLAN_PATHS[kind]}`} className="inline-flex items-center rounded-[var(--radius-control)] bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-deep">
            Add {labels.one.toLowerCase()}
          </Link>
        )}
      </div>
      {list.length === 0 ? (
        <Alert tone="info">{archived ? `No archived ${labels.many.toLowerCase()}.` : `No ${labels.many.toLowerCase()} yet.`}</Alert>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {list.map((p) => (
            <Link key={p.id} to={`/catalog/plan/${p.id}`} className="block">
              <Panel className="h-full p-5 transition hover:border-brand">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-display text-lg">{p.name}</h2>
                  <span className="shrink-0 font-semibold tabular-nums">{priceLabel(p)}</span>
                </div>
                <ul className="mt-2 flex flex-col gap-1 text-sm text-ink-soft">
                  {p.benefits.map((b) => (
                    <li key={b.id}>• {describeBenefit(b, p.kind, nameOf)}</li>
                  ))}
                </ul>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.kind === "membership" && p.minimumMonths > 0 && <Badge tone="brand">{p.minimumMonths}-month minimum</Badge>}
                  {p.kind === "membership" && p.noticeDays > 0 && <Badge tone="off">{p.noticeDays} days' notice</Badge>}
                  {!p.soldOnline && <Badge tone="off">Not sold online</Badge>}
                </div>
              </Panel>
            </Link>
          ))}
        </div>
      )}
      {archivedCount > 0 && (
        <button className="self-start text-sm font-medium text-muted hover:text-ink" onClick={() => setArchived((v) => !v)}>
          {archived ? `Show active ${labels.many.toLowerCase()}` : `Show archived (${archivedCount})`}
        </button>
      )}
    </div>
  );
}

const blank = (kind: PlanKind): PlanInput => ({
  kind,
  name: "",
  description: "",
  priceCents: 0,
  soldOnline: true,
  minimumMonths: 0,
  noticeDays: 0,
  agreementText: "",
  benefits: [{ kind: "voucher", quantity: 1, itemIds: [], expiresMonths: kind === "membership" ? 1 : null, minDaysBetween: null }],
});

const toInput = (p: Plan): PlanInput => ({
  kind: p.kind,
  name: p.name,
  description: p.description,
  priceCents: p.priceCents,
  soldOnline: p.soldOnline,
  minimumMonths: p.minimumMonths,
  noticeDays: p.noticeDays,
  agreementText: p.agreementText,
  benefits: p.benefits.map(({ id: _id, ...b }) => b),
});

export function PlanPage() {
  const { id, kind: kindPath } = useParams();
  const catalog = useCatalog();
  const plans = usePlans();
  if (catalog.isLoading || plans.isLoading) return <Loading />;
  if (catalog.error || !catalog.data || plans.error || !plans.data) return <Alert>{errorMessage(catalog.error ?? plans.error)}</Alert>;
  const existing = id ? plans.data.find((p) => p.id === id) : undefined;
  if (id && !existing) return <Alert>That plan doesn't exist.</Alert>;
  const kind = existing?.kind ?? PATH_KINDS[kindPath ?? ""];
  if (!kind) return <Alert>Unknown plan type.</Alert>;
  return <PlanForm key={id ?? kindPath} kind={kind} plan={existing} data={catalog.data} />;
}

function PlanForm({ kind, plan, data }: { kind: PlanKind; plan?: Plan; data: CatalogResponse }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const manage = can("catalog.manage");
  const initial = useMemo(() => (plan ? toInput(plan) : blank(kind)), [plan, kind]);
  const [v, setV] = useState<PlanInput>(initial);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const labels = ITEM_TYPE_LABELS[kind];
  const set = <K extends keyof PlanInput>(k: K, value: PlanInput[K]) => {
    setNotice(null);
    setV((cur) => ({ ...cur, [k]: value }));
  };
  const setBenefit = (i: number, b: PlanBenefitInput) => set("benefits", v.benefits.map((x, j) => (j === i ? b : x)));
  const dirty = JSON.stringify(v) !== JSON.stringify(initial);

  const save = useMutation({
    mutationFn: () => (plan ? put<Plan>(`/api/plans/${plan.id}`, v) : post<Plan>("/api/plans", v)),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ["plans"] });
      await qc.invalidateQueries({ queryKey: ["catalog"] });
      if (!plan) navigate(`/catalog/plan/${saved.id}`, { replace: true });
      setNotice(`${labels.one} saved.`);
    },
  });
  const archive = useMutation({
    mutationFn: () => post<Plan>(`/api/plans/${plan!.id}/${plan!.status === "active" ? "archive" : "restore"}`),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ["plans"] });
      setNotice(saved.status === "archived" ? "Archived. It can't be sold any more; clients who have it keep it." : "Restored.");
    },
  });

  return (
    <div className="max-w-3xl">
      <BackLink to={`/catalog/${PLAN_PATHS[kind]}`} label={labels.many} />
      <header className="mt-2 mb-6 flex flex-wrap items-center gap-3">
        <h1 className="page-title">{plan ? plan.name : `New ${labels.one.toLowerCase()}`}</h1>
        {plan?.status === "archived" && <Badge tone="off">Archived</Badge>}
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <fieldset disabled={!manage} className="flex flex-col gap-6">
          <Section title="Details">
            <Field label="Name" required maxLength={80} value={v.name} onChange={(e) => set("name", e.target.value)} />
            <TextAreaField label="Description" maxLength={1000} hint="Shown to clients when they sign up." value={v.description} onChange={(e) => set("description", e.target.value)} />
            <MoneyField className="max-w-64" label={kind === "membership" ? "Price per month" : "Price"} cents={v.priceCents} onChange={(c) => set("priceCents", c ?? 0)} />
            <Checkbox label="Sold online" description="Clients can sign up on its online page. Staff can always sell it." checked={v.soldOnline} onChange={(on) => set("soldOnline", on)} />
            {plan?.joinUrl && v.soldOnline && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted">Sign-up link:</span>
                <a href={plan.joinUrl} target="_blank" rel="noreferrer" className="min-w-0 truncate font-medium text-brand underline">
                  {plan.joinUrl}
                </a>
                <Button type="button" variant="quiet" onClick={() => void navigator.clipboard.writeText(plan.joinUrl!)}>
                  Copy
                </Button>
              </div>
            )}
          </Section>

          <Section
            title="Benefits"
            description={
              kind === "membership"
                ? "Vouchers are issued with each paid month. Vouchers and discounts apply only at clinic visits."
                : "Vouchers are issued when it's bought. They apply only at clinic visits."
            }
          >
            {v.benefits.map((b, i) => (
              <BenefitEditor
                key={i}
                kind={kind}
                benefit={b}
                data={data}
                onChange={(nb) => setBenefit(i, nb)}
                onRemove={v.benefits.length > 1 ? () => set("benefits", v.benefits.filter((_, j) => j !== i)) : undefined}
              />
            ))}
            {manage && (
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => set("benefits", [...v.benefits, { kind: "voucher", quantity: 1, itemIds: [], expiresMonths: kind === "membership" ? 1 : null, minDaysBetween: null }])}>
                  Add vouchers
                </Button>
                <Button type="button" onClick={() => set("benefits", [...v.benefits, { kind: "discount", percent: 20, amountCents: null, categoryIds: [], itemIds: [], excludeItemIds: [] }])}>
                  Add a discount
                </Button>
              </div>
            )}
          </Section>

          {kind === "membership" && (
            <Section title="Terms" description="Cancelling takes effect at the later of the end of the minimum and the first billing date after the notice period.">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Minimum commitment (months)" type="number" min={0} max={36} value={v.minimumMonths} onChange={(e) => set("minimumMonths", Number(e.target.value) || 0)} />
                <Field label="Notice to cancel (days before a billing date)" type="number" min={0} max={90} value={v.noticeDays} onChange={(e) => set("noticeDays", Number(e.target.value) || 0)} />
              </div>
            </Section>
          )}

          <Section
            title="Client agreement"
            description={`${kind === "membership" ? "Clients" : "Clients (if there's an agreement)"} sign this when they sign up; their signed copy is kept with their ${labels.one.toLowerCase()}. Separate paragraphs with a blank line; **bold** a lead-in with two asterisks.`}
          >
            <div className="flex gap-2">
              <Button type="button" variant={preview ? "quiet" : "secondary"} onClick={() => setPreview(false)}>
                Edit
              </Button>
              <Button type="button" variant={preview ? "secondary" : "quiet"} onClick={() => setPreview(true)}>
                Preview
              </Button>
            </div>
            {preview ? (
              v.agreementText ? (
                <div className="rounded-[var(--radius-control)] border border-line p-4">
                  <AgreementText text={v.agreementText} />
                </div>
              ) : (
                <p className="text-sm text-muted">No agreement yet.</p>
              )
            ) : (
              <TextAreaField label="Agreement" rows={14} maxLength={20000} value={v.agreementText} onChange={(e) => set("agreementText", e.target.value)} />
            )}
          </Section>
        </fieldset>

        {manage && (
          <div className="sticky bottom-0 mt-6 flex flex-col gap-3 border-t border-line bg-paper/95 py-4 backdrop-blur">
            {(save.error || archive.error) && <Alert>{errorMessage(save.error ?? archive.error)}</Alert>}
            {notice && !dirty && <Alert tone="ok">{notice}</Alert>}
            {plan && dirty && <p className="text-sm text-muted">Changes apply to new sign-ups and to vouchers issued from now on. Clients keep the agreement they signed.</p>}
            <div className="flex flex-wrap items-center justify-between gap-3">
              {plan ? (
                <Button type="button" variant={plan.status === "active" ? "danger" : "secondary"} busy={archive.isPending} onClick={() => archive.mutate()}>
                  {plan.status === "active" ? "Archive" : "Restore"}
                </Button>
              ) : (
                <span />
              )}
              <Button variant="primary" type="submit" busy={save.isPending} disabled={!dirty}>
                {plan ? "Save changes" : `Add ${labels.one.toLowerCase()}`}
              </Button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

function BenefitEditor({ kind, benefit: b, data, onChange, onRemove }: { kind: PlanKind; benefit: PlanBenefitInput; data: CatalogResponse; onChange: (b: PlanBenefitInput) => void; onRemove?: () => void }) {
  const bookable = data.items.filter((i) => (i.type === "service" || i.type === "add_on") && (i.status === "active" || b.itemIds.includes(i.id)));
  const sellable = data.items.filter((i) => (i.type === "service" || i.type === "add_on" || i.type === "product") && i.status === "active");
  return (
    <div className="rounded-[var(--radius-control)] border border-line p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="font-semibold">{b.kind === "voucher" ? "Vouchers" : "Discount"}</h3>
        {onRemove && (
          <Button type="button" variant="quiet" onClick={onRemove}>
            Remove
          </Button>
        )}
      </div>
      {b.kind === "voucher" ? (
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={kind === "membership" ? "How many per month" : "How many"} type="number" min={1} max={100} required value={b.quantity} onChange={(e) => onChange({ ...b, quantity: Number(e.target.value) || 1 })} />
            <SelectField label="Expire" value={b.expiresMonths ?? ""} onChange={(e) => onChange({ ...b, expiresMonths: e.target.value ? Number(e.target.value) : null })}>
              {VOUCHER_EXPIRY_CHOICES.map((c) => (
                <option key={c.label} value={c.months ?? ""}>
                  {c.label}
                </option>
              ))}
            </SelectField>
            <Field
              label="At most one every (days)"
              type="number"
              min={1}
              max={90}
              placeholder="No limit"
              hint="Clinical limit, e.g. 7"
              value={b.minDaysBetween ?? ""}
              onChange={(e) => onChange({ ...b, minDaysBetween: e.target.value ? Number(e.target.value) : null })}
            />
          </div>
          <ItemPicker title="Each voucher covers one of" items={bookable} selected={b.itemIds} onChange={(itemIds) => onChange({ ...b, itemIds })} />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Percent off"
              type="number"
              min={0}
              max={100}
              step="0.01"
              placeholder="—"
              value={b.percent ?? ""}
              onChange={(e) => onChange({ ...b, percent: e.target.value ? Number(e.target.value) : null, amountCents: e.target.value ? null : b.amountCents })}
            />
            <MoneyField label="Or an amount off each item" optional placeholder="—" cents={b.amountCents} onChange={(c) => onChange({ ...b, amountCents: c, percent: c !== null ? null : b.percent })} />
          </div>
          <fieldset>
            <legend className="mb-1 text-sm font-semibold text-ink-soft">Every item in</legend>
            <div className="grid gap-x-4 sm:grid-cols-3">
              {data.categories.map((c) => (
                <Checkbox
                  key={c.id}
                  label={c.name}
                  checked={b.categoryIds.includes(c.id)}
                  onChange={(on) => onChange({ ...b, categoryIds: on ? [...b.categoryIds, c.id] : b.categoryIds.filter((x) => x !== c.id) })}
                />
              ))}
            </div>
          </fieldset>
          <ItemPicker title="And these items" items={sellable.filter((i) => !i.categoryId || !b.categoryIds.includes(i.categoryId))} selected={b.itemIds} onChange={(itemIds) => onChange({ ...b, itemIds })} />
          <ItemPicker title="Except" items={sellable.filter((i) => i.categoryId && b.categoryIds.includes(i.categoryId))} selected={b.excludeItemIds} onChange={(excludeItemIds) => onChange({ ...b, excludeItemIds })} empty="Pick a category first." />
        </div>
      )}
    </div>
  );
}

/** Pick items: chosen ones as chips, the rest in a filterable list. */
function ItemPicker({ title, items, selected, onChange, empty }: { title: string; items: CatalogItem[]; selected: string[]; onChange: (ids: string[]) => void; empty?: string }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const chosen = selected.map((id) => items.find((i) => i.id === id)).filter((i): i is CatalogItem => !!i);
  const rest = items.filter((i) => !selected.includes(i.id) && (!q || i.name.toLowerCase().includes(q.toLowerCase())));
  return (
    <fieldset>
      <legend className="mb-1 text-sm font-semibold text-ink-soft">{title}</legend>
      {items.length === 0 ? (
        <p className="text-sm text-muted">{empty ?? "Nothing to pick."}</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {chosen.map((i) => (
              <span key={i.id} className="inline-flex items-center gap-1 rounded-full bg-mist px-3 py-1 text-sm">
                {i.name}
                <button type="button" className="text-muted hover:text-danger" aria-label={`Remove ${i.name}`} onClick={() => onChange(selected.filter((x) => x !== i.id))}>
                  ×
                </button>
              </span>
            ))}
            <button type="button" className="rounded-full border border-dashed border-line px-3 py-1 text-sm text-muted hover:text-ink" onClick={() => setOpen((o) => !o)}>
              {open ? "Done" : "+ Add"}
            </button>
          </div>
          {open && (
            <div className="mt-2 rounded-[var(--radius-control)] border border-line p-2">
              <Field label="Find" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" />
              <div className="mt-1 max-h-60 overflow-y-auto">
                {rest.map((i) => (
                  <button key={i.id} type="button" className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-mist" onClick={() => onChange([...selected, i.id])}>
                    {i.name}
                    <span className="text-muted"> · {ITEM_TYPE_LABELS[i.type].one}</span>
                  </button>
                ))}
                {rest.length === 0 && <p className="px-2 py-1.5 text-sm text-muted">Nothing left to add.</p>}
              </div>
            </div>
          )}
        </>
      )}
    </fieldset>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <Panel>
      <PanelHeader title={title} description={description} />
      <div className="flex flex-col gap-4 px-5 py-5">{children}</div>
    </Panel>
  );
}
